import cuid from "cuid";
import { filter, find, get, isEmpty, map } from "lodash";
import omitDeep from "omit-deep-lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import {
  CREATE_ATTACHMENT,
  DELETE_ATTACHMENT,
  GENERATE_SUBSCRIPTION_DOCUMENTS,
} from "utils/api/graphql/mutations/attachment";
import { DOWNLOAD_SINGLE_DOCUMENT } from "utils/api/graphql/mutations/download-document";
import { SUBSCRIPTION } from "utils/api/graphql/queries/subscription";
import {
  ATTACHMENT_TYPES,
  DOCUMENTS_TYPES,
  JOB_LIST,
  KEYS,
  PROCESS_TYPES,
  RELATIONSHIP,
  SOCIO_PROFESSIONAL_CATEGORIES,
} from "utils/constants";
import { downloadDocument } from "utils/helpers/files";
import Loader from "../Components/Loader";
import Navbar from "../Components/Navbar/Navbar";
import View from "./View";

export const DOCUMENTS_TYPE = {
  cin: DOCUMENTS_TYPES.CIN,
  paymentAccount: DOCUMENTS_TYPES.PAYMENT_ACCOUNT,
  refundAccount: DOCUMENTS_TYPES.REFUND_ACCOUNT,
  socialSecurityCertificate:
    ATTACHMENT_TYPES.HEALTH_INSURANCE_RIGHTS_CERTIFICATE,
  payerCin: DOCUMENTS_TYPES.PAYER_CIN,
  receiverCin: DOCUMENTS_TYPES.RECEIVER_CIN,
  proofOfAddress: DOCUMENTS_TYPES.PROOF_OF_ADDRESS,
  territorialCollectivityAttestation:
    ATTACHMENT_TYPES.TERRITORIAL_COLLECTIVITY_ATTESTATION,
  cnraclIrcantecAffiliation: ATTACHMENT_TYPES.CNRACL_IRCANTEC_AFFILIATION,
};

const JustificationTer = ({
  currentStatus,
  tracer,
  project,
  onBack,
  updateUrl,
  onNext,
}) => {
  const [initialValues, setInitialValues] = useState(null);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const TRANSLATION = t("B2C_MILTIS.JUSTIFICATION", { returnObjects: true });
  const subscriptionId =
    localStorage.getItem(KEYS.SUBSCRIPTION_ID) ||
    get(project, "fields.currentSubscription");

  const { data } = useQuery(SUBSCRIPTION, {
    variables: { where: { id: subscriptionId } },
  });
  const [downloadSingleDocument] = useMutation(DOWNLOAD_SINGLE_DOCUMENT);

  const [generateSubscriptionDocuments] = useMutation(
    GENERATE_SUBSCRIPTION_DOCUMENTS,
  );

  const supportingDocuments = useMemo(() => {
    const profile = get(data, "subscription.project.contact.fields.profile");
    return get(
      find(
        get(data, "subscription.insurancePlan.fields.supportingDocuments"),
        ({ profiles, processType }) =>
          processType === PROCESS_TYPES.B2C && profiles.includes(profile),
      ),
      "attachments",
      [],
    );
  }, [data]);

  // Get user ID for attachments query - wait for subscription data
  const userId = get(data, "subscription.project.contact.user.id");

  const {
    loading: attachmentLoading,
    data: attachmentsData,
    refetch: refetchAttachments,
  } = useQuery(graphql.queries.ATTACHMENTS, {
    fetchPolicy: "no-cache",
    // Skip the query until we have the user ID from subscription data
    skip: !userId,
    variables: {
      where: {
        user: {
          id: userId,
        },
        subscription: {
          id: subscriptionId,
        },
      },
      isIn: {
        type: [
          ...Object.values(DOCUMENTS_TYPE),
          ATTACHMENT_TYPES.TERRITORIAL_COLLECTIVITY_ATTESTATION,
          ATTACHMENT_TYPES.CNRACL_IRCANTEC_AFFILIATION,
        ],
      },
    },
  });

  // Transform attachments data into initialValues using useMemo
  // This ensures values are updated whenever attachmentsData changes (including after refetch)
  const computedInitialValues = useMemo(() => {
    const attachmentsList = get(attachmentsData, "attachments.data", []);

    if (!attachmentsList.length && !attachmentsData) {
      return null;
    }

    const mapAttachments = (attachments) =>
      map(attachments, ({ id, name, fileUrl }) => ({
        uid: id,
        name,
        status: "done",
        url: fileUrl,
      })) || [];

    return {
      cin: mapAttachments(
        filter(attachmentsList, ({ type }) => type === ATTACHMENT_TYPES.CIN),
      ),
      paymentAccount: mapAttachments(
        filter(
          attachmentsList,
          ({ type }) => type === ATTACHMENT_TYPES.PAYMENT_ACCOUNT,
        ),
      ),
      socialSecurityCertificate: mapAttachments(
        filter(
          attachmentsList,
          ({ type }) =>
            type === ATTACHMENT_TYPES.HEALTH_INSURANCE_RIGHTS_CERTIFICATE,
        ),
      ),
      payerCin: mapAttachments(
        filter(
          attachmentsList,
          ({ type }) => type === ATTACHMENT_TYPES.PAYER_CIN,
        ),
      ),
      refundAccount: mapAttachments(
        filter(
          attachmentsList,
          ({ type }) => type === ATTACHMENT_TYPES.REFUND_ACCOUNT,
        ),
      ),
      receiverCin: mapAttachments(
        filter(
          attachmentsList,
          ({ type }) => type === ATTACHMENT_TYPES.RECEIVER_CIN,
        ),
      ),
      proofOfAddress: mapAttachments(
        filter(
          attachmentsList,
          ({ type }) => type === ATTACHMENT_TYPES.PROOF_OF_ADDRESS,
        ),
      ),
      territorialCollectivityAttestation: mapAttachments(
        filter(
          attachmentsList,
          ({ type }) =>
            type === ATTACHMENT_TYPES.TERRITORIAL_COLLECTIVITY_ATTESTATION,
        ),
      ),
      cnraclIrcantecAffiliation: mapAttachments(
        filter(
          attachmentsList,
          ({ type }) => type === ATTACHMENT_TYPES.CNRACL_IRCANTEC_AFFILIATION,
        ),
      ),
    };
  }, [attachmentsData]);

  // Sync computedInitialValues to state for compatibility with existing code
  useEffect(() => {
    if (computedInitialValues) {
      setInitialValues(computedInitialValues);
    }
  }, [computedInitialValues]);

  // Determine payment conditions based on subscription data
  const paymentConditions = useMemo(() => {
    const subscriptionData = get(data, "subscription");
    const payment = get(subscriptionData, "payment");

    if (!payment) {
      return {
        isPayerReceiver: true,
        payerIsAdherent: true,
        receiverIsAdherent: true,
        requiresSubscriberPaysButDifferentReceiver: false, // Subscriber pays but doesn't receive reimbursements
        requiresThirdPartyPayer: false, // Third-party payer: Subscriber doesn't pay
        requiresBothThirdParty: false, // Subscriber doesn't pay AND doesn't receive
        requiresTwoSeparateRibs: false,
      };
    }

    // Check if payer and receiver are the same account
    const receiverId = get(payment, "receiver.id");
    const payerId = get(payment, "payer.id");
    const isPayerReceiver = receiverId === payerId;

    // Check if payer is the adherent (subscriber)
    const payerIsAdherent = get(payment, "payer.owner.isAdherent", false);

    // Check if receiver is the adherent (subscriber)
    const receiverIsAdherent = get(payment, "receiver.owner.isAdherent", false);

    // Subscriber pays premiums but doesn't receive reimbursements
    // Condition: payer is adherent AND receiver is NOT adherent AND payer ≠ receiver
    const requiresSubscriberPaysButDifferentReceiver =
      payerIsAdherent && !receiverIsAdherent && !isPayerReceiver;

    // Third-party payer: Subscriber doesn't pay premiums (someone else pays)
    // Condition: payer is NOT adherent
    const requiresThirdPartyPayer = !payerIsAdherent;

    // Subscriber doesn't pay AND doesn't receive
    // Condition: payer is NOT adherent AND receiver is NOT adherent AND payer ≠ receiver
    const requiresBothThirdParty =
      !payerIsAdherent && !receiverIsAdherent && !isPayerReceiver;

    // Requires 2 separate RIBs when payer and receiver are different accounts:
    // - Subscriber pays but different receiver (needs payer RIB + receiver RIB)
    // - Third-party payer with different receiver: needs payer RIB + receiver RIB
    // - Both third-party: needs payer RIB + receiver RIB
    const requiresTwoSeparateRibs = !isPayerReceiver;
    return {
      isPayerReceiver,
      payerIsAdherent,
      receiverIsAdherent,
      requiresSubscriberPaysButDifferentReceiver, // Requires: CIN adherent + CIN receiver + RIB receiver
      requiresThirdPartyPayer, // Requires: CIN adherent + CIN payer
      requiresBothThirdParty, // Requires: 3 CINs (adherent + payer + receiver) + 2 RIBs
      requiresTwoSeparateRibs, // Requires: 2 RIBs (payer RIB + receiver RIB)
    };
  }, [data]);

  const [addAttachment] = useMutation(CREATE_ATTACHMENT);
  const [deleteAttachment] = useMutation(DELETE_ATTACHMENT);

  // Determine professional situation conditions based on subscription data
  const professionalSituationConditions = useMemo(() => {
    const professionalSituation = get(
      data,
      "subscription.project.contact.fields.professionalSituation",
    );
    const profession = get(data, "subscription.project.contact.profession");

    // Check if profession is EXECUTIVES_AND_PUBLIC_SERVICE_EMPLOYEES (required for CA4 and CA5 documents)
    const isPublicServiceEmployee =
      profession ===
      SOCIO_PROFESSIONAL_CATEGORIES.EXECUTIVES_AND_PUBLIC_SERVICE_EMPLOYEES;

    if (!professionalSituation || !isPublicServiceEmployee) {
      return {
        professionalSituation: professionalSituation || null,
        profession: profession || null,
        isPublicServiceEmployee,
        isActive: false, // "En activité" - requires territorial collectivity attestation
        isRetired: false, // "Retraité" - requires CNRACL/IRCANTEC affiliation
        requiresTerritorialCollectivityAttestation: false,
        requiresCnraclIrcantecAffiliation: false,
      };
    }

    // Check professional situation type
    const isActive = professionalSituation === JOB_LIST.ACTIVE;
    const isRetired = professionalSituation === JOB_LIST.RETIRED;

    return {
      professionalSituation,
      profession,
      isPublicServiceEmployee,
      isActive,
      isRetired,
      // CA4: Attestation de la collectivité territoriale - Required when "En activité" AND profession is public service
      requiresTerritorialCollectivityAttestation:
        isActive && isPublicServiceEmployee,
      // CA5: Justificatif d'affiliation CNRACL ou IRCANTEC - Required when "Retraité" AND profession is public service
      requiresCnraclIrcantecAffiliation: isRetired && isPublicServiceEmployee,
    };
  }, [data]);

  // Track previous professionalSituation to detect actual changes
  const prevProfessionalSituation = useRef(
    professionalSituationConditions.professionalSituation,
  );

  // Refetch attachments when professionalSituation changes
  // The initial fetch is handled by the useQuery hook once userId is available
  useEffect(() => {
    const currentProfessionalSituation =
      professionalSituationConditions.professionalSituation;
    // Only refetch if professionalSituation actually changed (not on initial mount)
    if (
      prevProfessionalSituation.current !== currentProfessionalSituation &&
      refetchAttachments &&
      userId &&
      attachmentsData
    ) {
      refetchAttachments();
    }
    prevProfessionalSituation.current = currentProfessionalSituation;
  }, [
    professionalSituationConditions.professionalSituation,
    refetchAttachments,
    userId,
    attachmentsData,
  ]);

  const handleFileRemove = (fieldName) => async (item) => {
    const fileId = item?.uid || item?.id;

    // If file is already uploaded (has an id and doesn't contain "AUTO"), delete it
    if (fileId && typeof fileId === "string" && !fileId.includes("AUTO")) {
      try {
        await deleteAttachment({
          variables: {
            where: { id: fileId },
          },
        });
        // Refetch attachments to update the UI
        if (refetchAttachments) {
          await refetchAttachments();
        }
      } catch (error) {
        console.error("Error deleting attachment:", error);
      }
    }

    // Return the field name so View.js can update the form values
    return fieldName;
  };

  const onSubmit = async (documents) => {
    setDocumentsLoading(true);
    const documentsToUpload = [];
    Object.entries(documents)?.forEach(([key, attachments]) => {
      if (Array.isArray(attachments) && attachments.length > 0) {
        attachments
          .filter((e) => !e.status)
          .forEach(({ name, contentType, base64 }) => {
            documentsToUpload.push({
              id: cuid(),
              subscription: { id: subscriptionId },
              name,
              content: base64,
              contentType,
              type: DOCUMENTS_TYPE[key],
              user: { id: get(data, "subscription.project.contact.user.id") },
            });
          });
      }
    });
    await Promise.all(
      documentsToUpload.map((document, index) =>
        addAttachment({
          variables: {
            data: document,
          },
        }),
      ),
    );
    const source = localStorage.getItem(KEYS.SOURCE);
    updateUrl({
      subscriptionId,
      url: get(
        data,
        "subscription.insurancePlan.config.generatedUrl",
        `${process.env.REACT_APP_BASE_URL}/#/public/process/init/${source}`,
      ),
    });
    const relatedPersons = get(project, "contact.relatedPersons", []).filter(
      (_) => _,
    );
    const hasChildren = !!relatedPersons.filter(
      ({ relationship }) => relationship === RELATIONSHIP.CHILD,
    ).length;
    const hasSpouse = !isEmpty(
      relatedPersons.find(
        ({ relationship }) => relationship === RELATIONSHIP.SPOUSE,
      ),
    );
    if (!get(data, "subscription.locked"))
      return generateSubscriptionDocuments({
        onCompleted: (_) => {
          onNext({
            payload: {},
            onCompleted: () => setDocumentsLoading(false),
          });
        },
        variables: {
          data: { id: subscriptionId },
        },
      });
    onNext({
      payload: {
        fields: {
          ...get(project, "fields"),
          hasRelatedPersons: {
            hasChildren,
            hasSpouse,
          },
        },
      },
      onCompleted: () => setDocumentsLoading(false),
    });
  };
  const downloadFile = async (id) => {
    await downloadSingleDocument({
      variables: { where: { id } },
      onCompleted: (_) => {
        const { document } = omitDeep(_.downloadSingleDocument, "__typename");
        downloadDocument(document);
      },
    });
  };
  // Show loader while waiting for subscription data, attachments, or initial values
  if (!userId || attachmentLoading || isEmpty(initialValues) || isEmpty(data))
    return <Loader />;

  return (
    <>
      <Navbar
        project={project}
        progress={currentStatus?.progress}
        tracer={tracer}
        showProfile={true}
        showCart={true}
        step={4}
        stepCount={5}
        title="Mes informations contractuelles"
      />
      <View
        onSubmit={onSubmit}
        supportingDocuments={supportingDocuments}
        initialValues={initialValues}
        translate={TRANSLATION}
        downloadFile={downloadFile}
        isRia={get(data, "subscription.project.ria") === true}
        onBack={onBack}
        paymentConditions={paymentConditions}
        subscription={get(data, "subscription")}
        project={project}
        onFileRemove={handleFileRemove}
        professionalSituationConditions={professionalSituationConditions}
        documentsLoading={documentsLoading}
      />
    </>
  );
};
export default JustificationTer;
